import React from "react";

import { businessHealthScorePalette } from "../../../../Utils/Helper/businessHealth";

// TypeScript prop definition
export type OverallSectionProps = {
  sectionData: any;
  organizationName?: string;
  data?: any;
  audioContent?: string;
  onToggleAudio?: () => void;
};

function getHealthBadgeColor(flag: string) {
  switch (flag?.toUpperCase()) {
    case "G":
      return { color: "#166534", bgColor: "#dcfce7", label: "GREEN" };
    case "Y":
      return { color: "#a16207", bgColor: "#fef3c7", label: "YELLOW" };
    case "R":
      return { color: "#991b1b", bgColor: "#fee2e2", label: "RED" };
    default:
      return { color: "#374151", bgColor: "#f3f4f6", label: "UNKNOWN" };
  }
}
function getRelevanceBadgeColor(flag: string) {
  switch (flag?.toUpperCase()) {
    case "H":
      return { color: "#6b21a8", bgColor: "#f3e8ff", label: "HIGH" };
    case "M":
      return { color: "#1e40af", bgColor: "#dbeafe", label: "MEDIUM" };
    case "L":
      return { color: "#374151", bgColor: "#f3f4f6", label: "LOW" };
    default:
      return { color: "#374151", bgColor: "#f3f4f6", label: "UNKNOWN" };
  }
}
function getScoreCircleColor(score: number) {
  const p = businessHealthScorePalette(score);
  return { light: p.outer, medium: p.inner, dark: p.score };
}

/** Score Bubble */
const ScoreBubble: React.FC<{ score: number }> = ({ score }) => {
  const color = getScoreCircleColor(score);
  return (
    <div
      style={{
        position: "relative",
        width: 50,
        height: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          position: "absolute",
          width: 50,
          height: 50,
          borderRadius: "50%",
          backgroundColor: color.light,
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 36,
          height: 36,
          borderRadius: "50%",
          backgroundColor: color.medium,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: -2,
          right: -2,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: color.dark,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px solid white",
        }}
      >
        <span
          style={{
            color: "white",
            fontSize: 11,
            fontWeight: 700,
            lineHeight: "1",
          }}
        >
          {Math.round(score)}
        </span>
      </div>
    </div>
  );
};

/** Health Badge */
const HealthBadge: React.FC<{ flag: string }> = ({ flag }) => {
  const badge = getHealthBadgeColor(flag);
  return (
    <span
      style={{
        background: badge.color,
        borderRadius: 20,
        color: "white",
        padding: "6px 12px",
        fontSize: 13,
        fontWeight: 500,
        display: "inline-flex",
        alignItems: "center",
        marginRight: 8,
      }}
    >
      Account Health: {badge.label}
    </span>
  );
};

/** Relevance Badge */
const RelevanceBadge: React.FC<{ flag: string; organizationName?: string }> = ({
  flag,
  organizationName,
}) => {
  const badge = getRelevanceBadgeColor(flag);
  return (
    <span
      style={{
        background: "#8b5cf6",
        borderRadius: 20,
        color: "white",
        padding: "6px 12px",
        fontSize: 13,
        fontWeight: 500,
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      Relevance to {organizationName}: {badge.label}
    </span>
  );
};

/** Audio button */
const AudioButton: React.FC<{
  onToggleAudio?: () => void;
}> = ({ onToggleAudio }) => (
  <button
    id="overall-audio-btn"
    title="Play overall audio narration"
    onClick={onToggleAudio}
    style={{
      background: "none",
      border: "none",
      padding: 8,
      borderRadius: 6,
      cursor: "pointer",
      transition: "background-color 0.2s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#8b5cf6",
    }}
  >
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#8b5cf6"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 5 6 9H2v6h4l5 4V5Z" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  </button>
);

/** Modal Header */
const ModalHeader: React.FC<{
  score: number;
  accountName: string;
  onToggleAudio?: () => void;
}> = ({ score, accountName, onToggleAudio }) => (
  <div
    style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}
  >
    <ScoreBubble score={score} />
    <div>
      <h3 style={{ margin: 0, fontSize: 18, color: "#8b5cf6" }}>
        <strong>{accountName}</strong> - <em>Overall Update</em>
      </h3>
    </div>
    <AudioButton onToggleAudio={onToggleAudio} />
  </div>
);

// Card: Overall Summary
const OverallSummaryCard: React.FC<{ summary: string }> = ({ summary }) =>
  summary && summary !== "No summary statement available" ? (
    <div
      style={{
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        padding: 16,
      }}
    >
      <h3
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: "#0f172a",
          margin: "0 0 12px 0",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        {/* (SVG icon simplified for demo) */}
        <span role="img" aria-label="summary">
          📝
        </span>
        Overall Summary
      </h3>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: "#374151" }}>
        {summary}
      </div>
    </div>
  ) : null;

// Card: Relevance Statement
const RelevanceCard: React.FC<{
  relevance: string;
  organizationName?: string;
}> = ({ relevance, organizationName }) =>
  relevance ? (
    <div
      style={{
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        padding: 16,
      }}
    >
      <h3
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: "#0f172a",
          margin: "0 0 12px 0",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span role="img" aria-label="relevance">
          🟣
        </span>
        Relevance{organizationName ? ` to ${organizationName}` : ""}
      </h3>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: "#374151" }}>
        {relevance}
      </div>
    </div>
  ) : null;

// Main Component
export const OverallSection: React.FC<OverallSectionProps> = ({
  sectionData,
  organizationName,
  data,
  audioContent,
  onToggleAudio,
}) => {
  if (!sectionData) return <div>No overall data available.</div>;
  // Use fallbacks
  const overallFlag = sectionData.overallFlag || "Y";
  const overallScore =
    sectionData.overallScore !== undefined ? sectionData.overallScore : 50;
  const overallRelevanceFlag = sectionData.overallRelevanceFlag || "M";
  const summaryStatement =
    sectionData.summaryStatement || "No summary statement available";
  const relevanceStatement = sectionData.relevanceStatement || null;
  const accountName =
    sectionData.accountName || data?.accountName || "Unknown Account";

  const handleToggleAudio = () => {
    if (onToggleAudio) {
      onToggleAudio();
    }
  };

  return (
    <div
      className="overall-section-consolidated"
      data-audio-content={audioContent || ""}
      data-modal-header={accountName}
      style={{ maxHeight: "60vh", overflowY: "auto" }}
    >
      {/* Modal header */}
      <ModalHeader
        score={overallScore}
        accountName={accountName}
        onToggleAudio={handleToggleAudio}
      />
      {/* Badges row */}
      <div
        style={{
          background: "#f8fafc",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          padding: "12px 16px",
          marginBottom: 16,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <HealthBadge flag={overallFlag} />
          <RelevanceBadge
            flag={overallRelevanceFlag}
            organizationName={organizationName}
          />
        </div>
      </div>
      {/* Summary + Relevance cards */}
      <div style={{ display: "grid", gap: 20 }}>
        <OverallSummaryCard summary={summaryStatement} />
        <RelevanceCard
          relevance={relevanceStatement}
          organizationName={organizationName}
        />
      </div>
    </div>
  );
};
